import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const REPUBLISH_EVT = "ddi:pending-republish-changed";

/**
 * Fire this from any place that mutates content on the doctor
 * (credential add/update/delete, SEO save, article publish/unpublish,
 * gallery add/delete) so the banner re-checks the doctor state
 * without a route change or hard reload.
 */
export function notifyPendingRepublishChanged() {
    window.dispatchEvent(new CustomEvent(REPUBLISH_EVT));
}

/**
 * Republish banner — appears on Phase 6 pages when the doctor is currently
 * `published` and has `pending_republish=true`. Reactively refreshes on
 * a custom event so it appears immediately after inline edits, not only
 * on route change.
 */
export default function RepublishBanner({ compact = false }) {
    const [state, setState] = useState(null);

    async function refresh() {
        try {
            const r = await api.get("/me/doctor");
            setState(r.data);
        } catch {
            // silent — banner just won't show
        }
    }

    useEffect(() => {
        refresh();
        const onEvt = () => refresh();
        const onFocus = () => refresh();
        window.addEventListener(REPUBLISH_EVT, onEvt);
        window.addEventListener("focus", onFocus);
        // Fallback: periodic re-check every 30s while page is open
        const iv = setInterval(refresh, 30000);
        return () => {
            window.removeEventListener(REPUBLISH_EVT, onEvt);
            window.removeEventListener("focus", onFocus);
            clearInterval(iv);
        };
    }, []);

    if (!state) return null;
    if (state.profile_status !== "published") return null;
    if (!state.pending_republish) return null;
    return (
        <div
            className="mb-6 flex items-center gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#FEF3C7] px-4 py-3"
            data-testid="republish-banner"
        >
            <AlertTriangle size={18} className="text-[#B45309] shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#92400E]">
                    Republish needed
                </div>
                {!compact && (
                    <div className="text-[12px] text-[#92400E]/80">
                        You have changes since your last publish
                        {state.pending_republish_reason ? (
                            <> · reason: <span className="font-mono">{state.pending_republish_reason}</span></>
                        ) : null}
                        . They won't reach your public profile until you republish.
                    </div>
                )}
            </div>
            <Link
                to="/doctor/publish"
                className="ddi-btn-primary text-[13px] whitespace-nowrap"
                data-testid="republish-banner-cta"
            >
                <RefreshCw size={14} /> Republish now
            </Link>
        </div>
    );
}
